import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

// Zelligio internal Reports & Feedback review queue (COP-ARCH-207…209): platform-wide
// visibility with full Dealer/Location context, internal notes, assignment and
// auditable status transitions.
const STATES = ["New", "Reviewing", "Resolved", "Dismissed"];

export default function AdminReports() {
  const [reports, setReports] = useState(null);
  const [filter, setFilter] = useState(null);
  const [note, setNote] = useState({});

  const load = useCallback(async () => {
    try {
      const r = await api.get("/reports/admin/all", { params: filter ? { status: filter } : {} });
      setReports(r.data.reports || []);
    } catch {
      setReports([]);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const update = async (ref, payload) => {
    try {
      await api.patch(`/reports/admin/${ref}`, payload);
      toast.success(`${ref} updated`);
      load();
    } catch {
      toast.error("Update failed.");
    }
  };

  return (
    <div data-testid="admin-reports-page">
      <div className="mb-6">
        <h1 className="font-display text-4xl font-light tracking-tight text-charcoal md:text-5xl">Reports &amp; Feedback</h1>
        <p className="mt-1 text-sm text-warmgrey">Platform-wide internal review queue — dealer reports with full context.</p>
      </div>

      <div className="mb-4 flex flex-wrap gap-2" data-testid="admin-report-filters">
        <button onClick={() => setFilter(null)}
          className={`rounded-full border px-3 py-1 text-xs ${!filter ? "border-terracotta bg-terracotta text-white" : "border-border text-charcoal"}`}
          data-testid="admin-report-filter-all">All</button>
        {STATES.map((s) => (
          <button key={s} onClick={() => setFilter(s)}
            className={`rounded-full border px-3 py-1 text-xs ${filter === s ? "border-terracotta bg-terracotta text-white" : "border-border text-charcoal"}`}
            data-testid={`admin-report-filter-${s.toLowerCase()}`}>{s}</button>
        ))}
      </div>

      {reports === null && <p className="text-sm text-warmgrey">Loading…</p>}
      {reports?.length === 0 && <p className="rounded-md border border-border bg-white p-6 text-sm text-warmgrey" data-testid="admin-reports-empty">No reports.</p>}

      <div className="space-y-3">
        {(reports || []).map((r) => (
          <div key={r.id} className="rounded-md border border-border bg-white p-4" data-testid="admin-report-card">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-3">
                <span className="font-mono text-xs font-semibold text-charcoal" data-testid="admin-report-ref">{r.reference}</span>
                <span className="rounded-full border border-border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-warmgrey">{r.status}</span>
              </div>
              <span className="text-xs text-warmgrey">{new Date(r.created_at).toLocaleString()}</span>
            </div>
            <p className="mt-2 text-sm text-charcoal">{r.text}</p>
            <p className="mt-1 text-[11px] text-warmgrey">
              {r.dealer_name} · {r.user_name} ({r.user_role}) · {r.page} · {r.mode} mode · session {r.conversation?.session_id?.slice(-8)}
              {r.diagnostics?.prompt_version ? ` · ${r.diagnostics.prompt_version}` : ""}
            </p>
            {(r.history || []).length > 0 && (
              <div className="mt-2 border-t border-border pt-2">
                {r.history.map((h, i) => (
                  <p key={i} className="text-[11px] text-warmgrey">{new Date(h.at).toLocaleString()} — {h.actor_name || h.actor}: {h.action}{h.note ? ` (${h.note})` : ""}</p>
                ))}
              </div>
            )}
            {(r.internal_notes || []).length > 0 && (
              <div className="mt-2 rounded-sm bg-sand/60 p-2" data-testid="admin-internal-notes">
                {r.internal_notes.map((n, i) => (
                  <p key={i} className="text-[11px] italic text-warmgrey">Internal: {n.text}</p>
                ))}
              </div>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3">
              {STATES.filter((s) => s !== r.status).map((s) => (
                <Button key={s} variant="outline" className="h-7 border-border text-xs"
                  onClick={() => update(r.reference, { status: s })} data-testid={`admin-report-${s.toLowerCase()}-btn`}>{s}</Button>
              ))}
              <input
                value={note[r.id] || ""}
                onChange={(e) => setNote((p) => ({ ...p, [r.id]: e.target.value }))}
                placeholder="Internal note (Zelligio-only)…"
                className="h-7 flex-1 rounded-sm border border-border bg-background px-2 text-xs"
                data-testid="admin-internal-note-input"
              />
              <Button variant="outline" className="h-7 border-border text-xs"
                disabled={!(note[r.id] || "").trim()}
                onClick={() => { update(r.reference, { internal_note: note[r.id].trim() }); setNote((p) => ({ ...p, [r.id]: "" })); }}
                data-testid="admin-add-note-btn">Add note</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
